// Phase RV-6b — FLEX History Panel.
//
// Source: ROLLOVER Intelligence Enhancement v1.0 §11.3.1.
//
// Doctrine:
//   - Composes I0 primitives only (HairlineRow, Pill, Eyebrow, Badge, Stat).
//   - No primitive edits, no --sa-* token mutations.
//   - N15 absolute: zero references to chain mutation paths.
//   - N19 disclaimer lives on the builder panel; this panel renders history only.

import type { CSSProperties } from "react";
import { useState } from "react";
import { Badge, Eyebrow, HairlineRow, Pill } from "../super-acca";
import {
  type FlexSlip,
  type FlexSlipLeg,
  flexOutcomeBadge,
  useFlexHistory,
} from "../../rollover/useFlexHistory.ts";
import { BankWinModal } from "../sika-extras/BankWinModal.tsx";

const DEFAULT_VISIBLE = 10;

const TIER_ACCENT: Record<"2x" | "3x" | "4x" | "5x" | "6x", string> = {
  "2x": "var(--sa-accent-saffron)",
  "3x": "var(--sa-fg-muted)",
  "4x": "var(--sa-accent-saffron)",
  "5x": "var(--sa-accent-saffron)",
  "6x": "var(--sa-accent-saffron)",
};

const TIER_LABEL: Record<"2x" | "3x" | "4x" | "5x" | "6x", string> = {
  "2x": "2×",
  "3x": "3×",
  "4x": "4×",
  "5x": "5×",
  "6x": "6×",
};

export const FLEX_HISTORY_EMPTY_COPY =
  "No FLEX slips locked yet. Build your first one from the Upcoming Picks Board.";

export interface FlexHistoryPanelProps {
  isAuthed: boolean;
  style?: CSSProperties;
}

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleString("en-GB", {
      timeZone: "Africa/Accra",
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "—";
  }
}

function OutcomePill({ slip }: { slip: FlexSlip }): JSX.Element {
  const badge = flexOutcomeBadge(slip.status, slip.outcome);
  if (badge.kind === "pending") {
    return <Pill borderColor="var(--sa-hairline)">PENDING</Pill>;
  }
  if (badge.kind === "won") {
    const ret = slip.potential_return;
    return (
      <Badge variant="success">
        WON{ret !== null ? ` · GH₵${ret.toFixed(2)}` : ""}
      </Badge>
    );
  }
  if (badge.kind === "lost") {
    return <Badge variant="danger">LOST</Badge>;
  }
  if (badge.kind === "void") {
    return <Pill borderColor="var(--sa-hairline)">VOID</Pill>;
  }
  return <Badge variant="warn">PARTIAL</Badge>;
}

function LegDetailLine({ leg }: { leg: FlexSlipLeg }): JSX.Element {
  const accent = leg.tier !== null ? TIER_ACCENT[leg.tier] : "var(--sa-fg-muted)";
  const tierLabel = leg.tier !== null ? TIER_LABEL[leg.tier] : "—";
  const lo = leg.leg_outcome;
  return (
    <HairlineRow style={{ borderLeft: `3px solid ${accent}` }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          width: "100%",
          flexWrap: "wrap",
        }}
      >
        <Pill borderColor={accent} color={accent}>
          {tierLabel}
        </Pill>
        <div style={{ display: "flex", flexDirection: "column", minWidth: 0, flex: 1 }}>
          <div
            style={{
              fontFamily: "var(--sa-font-stat)",
              fontSize: 12,
              letterSpacing: "0.06em",
              color: "var(--sa-fg-primary)",
            }}
          >
            {leg.match_name ?? "—"}
          </div>
          <div
            style={{
              fontFamily: "var(--sa-font-mono)",
              fontSize: 10,
              color: "var(--sa-fg-muted)",
            }}
          >
            {leg.market ?? "—"} · {leg.pick ?? "—"}
          </div>
        </div>
        <div
          style={{
            fontFamily: "var(--sa-font-mono)",
            fontSize: 12,
            color: "var(--sa-fg-primary)",
          }}
        >
          {leg.odds !== null ? leg.odds.toFixed(2) : "—"}
        </div>
        {lo === "won" ? (
          <Badge variant="success">W</Badge>
        ) : lo === "lost" ? (
          <Badge variant="danger">L</Badge>
        ) : lo === "void" ? (
          <Pill borderColor="var(--sa-hairline)">V</Pill>
        ) : null}
      </div>
    </HairlineRow>
  );
}

function SlipRow({ slip }: { slip: FlexSlip }): JSX.Element {
  const [open, setOpen] = useState(false);
  const [bankOpen, setBankOpen] = useState(false);
  const isWon =
    slip.status === "settled" &&
    slip.outcome === "won" &&
    (slip.potential_return ?? 0) > 0;
  const oddsLabel = `${slip.leg_count} legs · ${slip.combined_odds.toFixed(2)}×`;
  const stakeLabel =
    slip.stake_amount !== null
      ? `GH₵${slip.stake_amount.toFixed(2)} → GH₵${(
          slip.potential_return ?? 0
        ).toFixed(2)}`
      : "—";
  return (
    <div data-testid={`flex-history-row-${slip.flex_slip_id}`}>
      <HairlineRow>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            width: "100%",
            flexWrap: "wrap",
          }}
        >
          <Pill borderColor="var(--sa-hairline)">
            {slip.window_label ?? "—"}
          </Pill>
          <div
            style={{
              fontFamily: "var(--sa-font-mono)",
              fontSize: 11,
              color: "var(--sa-fg-muted)",
              minWidth: 110,
            }}
            data-testid="flex-history-date"
          >
            {formatDate(slip.window_publication_at ?? slip.created_at)}
          </div>
          <div
            style={{
              fontFamily: "var(--sa-font-mono)",
              fontSize: 12,
              color: "var(--sa-fg-primary)",
              flex: 1,
              minWidth: 120,
            }}
            data-testid="flex-history-odds"
          >
            {oddsLabel}
          </div>
          <div
            className="flex-history-stake"
            style={{
              fontFamily: "var(--sa-font-mono)",
              fontSize: 11,
              color: "var(--sa-fg-muted)",
              minWidth: 140,
            }}
          >
            {stakeLabel}
          </div>
          <OutcomePill slip={slip} />
          {isWon ? (
            <button
              type="button"
              data-testid={`flex-history-bank-${slip.flex_slip_id}`}
              onClick={() => setBankOpen(true)}
              aria-label="Bank toward goal"
              style={{
                background: "transparent",
                border: "1px solid var(--sa-accent-saffron)",
                color: "var(--sa-accent-saffron)",
                padding: "2px 8px",
                borderRadius: 3,
                fontFamily: "var(--sa-font-mono)",
                fontSize: 11,
                letterSpacing: "0.08em",
                cursor: "pointer",
              }}
            >
              BANK TO GOAL
            </button>
          ) : null}
          <button
            type="button"
            data-testid={`flex-history-expand-${slip.flex_slip_id}`}
            onClick={() => setOpen((v) => !v)}
            aria-label={open ? "Collapse legs" : "Expand legs"}
            aria-expanded={open}
            style={{
              background: "transparent",
              border: "1px solid var(--sa-hairline)",
              color: "var(--sa-fg-muted)",
              padding: "2px 8px",
              borderRadius: 3,
              fontFamily: "var(--sa-font-mono)",
              fontSize: 12,
              cursor: "pointer",
            }}
          >
            {open ? "▾" : "▸"}
          </button>
        </div>
      </HairlineRow>
      {open ? (
        <div
          data-testid={`flex-history-legs-${slip.flex_slip_id}`}
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 4,
            paddingLeft: 16,
            paddingTop: 6,
            paddingBottom: 6,
          }}
        >
          {slip.legs.map((leg, i) => (
            <LegDetailLine key={`${slip.flex_slip_id}-${i}`} leg={leg} />
          ))}
        </div>
      ) : null}
      {bankOpen ? (
        <BankWinModal
          source="flex"
          sourceId={slip.flex_slip_id}
          amount={slip.potential_return ?? 0}
          sourceLabel={`FLEX ${slip.leg_count}-leg`}
          onClose={() => setBankOpen(false)}
        />
      ) : null}
    </div>
  );
}

export function FlexHistoryPanel({
  isAuthed,
  style,
}: FlexHistoryPanelProps): JSX.Element {
  const history = useFlexHistory();
  const [expanded, setExpanded] = useState(false);

  if (!isAuthed) {
    return (
      <section
        data-testid="flex-history-anon"
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 8,
          padding: 16,
          border: "1px solid var(--sa-hairline)",
          borderRadius: 4,
          ...style,
        }}
      >
        <Eyebrow>FLEX HISTORY</Eyebrow>
        <div
          style={{
            fontFamily: "var(--sa-font-mono)",
            fontSize: 12,
            color: "var(--sa-fg-muted)",
            lineHeight: 1.4,
          }}
        >
          Sign in to view your FLEX history.
        </div>
      </section>
    );
  }

  const total = history.slips.length;
  const visible = expanded ? history.slips : history.slips.slice(0, DEFAULT_VISIBLE);

  return (
    <section
      data-testid="flex-history-panel"
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 12,
        padding: 16,
        border: "1px solid var(--sa-hairline)",
        borderRadius: 4,
        ...style,
      }}
    >
      <header
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 8,
        }}
      >
        <Eyebrow>{`FLEX HISTORY · ${history.settledCount} SETTLED`}</Eyebrow>
        {total > DEFAULT_VISIBLE ? (
          <button
            type="button"
            data-testid="flex-history-toggle-all"
            onClick={() => setExpanded((v) => !v)}
            style={{
              background: "transparent",
              border: "none",
              color: "var(--sa-fg-muted)",
              fontFamily: "var(--sa-font-mono)",
              fontSize: 11,
              textDecoration: "underline",
              cursor: "pointer",
            }}
          >
            {expanded ? "Show fewer" : `View all (${total})`}
          </button>
        ) : null}
      </header>

      {total === 0 ? (
        <div
          data-testid="flex-history-empty"
          style={{
            padding: 16,
            border: "1px dashed var(--sa-hairline)",
            borderRadius: 4,
            color: "var(--sa-fg-muted)",
            fontFamily: "var(--sa-font-mono)",
            fontSize: 12,
            textAlign: "center",
          }}
        >
          {FLEX_HISTORY_EMPTY_COPY}
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {visible.map((slip) => (
            <SlipRow key={slip.flex_slip_id} slip={slip} />
          ))}
        </div>
      )}

      {history.error ? (
        <div
          data-testid="flex-history-error"
          style={{
            fontFamily: "var(--sa-font-mono)",
            fontSize: 11,
            color: "var(--sa-fg-danger, #b00020)",
          }}
        >
          {history.error}
        </div>
      ) : null}
    </section>
  );
}
